if (!window.LimoHeroSections) {
  window.LimoHeroSections = {};
}

class LimoHero {
  constructor(sectionEl) {
    this.sectionEl = sectionEl;
    this.root = sectionEl.querySelector('.limo-hero');
    this.viewport = sectionEl.querySelector('.limo-hero__track-wrapper');
    this.track = sectionEl.querySelector('[data-limo-hero-track]');
    this.cursor = sectionEl.querySelector('[data-limo-hero-cursor]');
    this.enableModuleFadeInOnScroll = sectionEl.dataset.enableModuleFadeInOnScroll === 'true';
    this.fadeInObserver = null;

    if (!this.root || !this.viewport || !this.track || !this.cursor) return;

    this.isHovered = false;
    this.isDragging = false;
    this.isTouchInteracting = false;
    this.baseSpeed = 0.4;
    this.maxExtraSpeed = 2.0;
    this.touchAutoSpeedMultiplier = 4.0;
    this.mouseXRatio = 0.5;
    this.rafId = null;
    this.autoScrollLeft = 0;
    this.lastFrameTime = 0;
    this.lastTouchMoveAt = 0;
    this.touchResumeDelayMs = 180;
    this.stallFrameCount = 0;
    this.stallFrameThreshold = 18;
    this.stallNudgePx = 1;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchMoveThreshold = 8;
    this.modeMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.isMouseMode = this.modeMediaQuery.matches;
    this.mouseEventsBound = false;
    this.loopBufferSets = 1;
    this.minScroll = 0;
    this.maxScroll = 0;
    this.maxNativeScroll = 0;
    this.setupRetryId = null;
    this.setupRetryCount = 0;
    this.maxSetupRetries = 60;

    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleWheel = this.handleWheel.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleMouseDrag = this.handleMouseDrag.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleGlobalTouchEnd = this.handleGlobalTouchEnd.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleInputModeChange = this.handleInputModeChange.bind(this);

    this.init();
  }

  init() {
    this.initModuleFadeInObserver();
    this.setupTrack();
    this.bindEvents();
    this.startAnimation();
  }

  initModuleFadeInObserver() {
    if (!this.enableModuleFadeInOnScroll) return;
    if (this.sectionEl.classList.contains('limo-hero-section--in-view')) return;
    if (typeof IntersectionObserver !== 'function') {
      this.sectionEl.classList.add('limo-hero-section--in-view');
      return;
    }

    this.fadeInObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.sectionEl.classList.add('limo-hero-section--in-view');
          if (this.fadeInObserver) {
            this.fadeInObserver.disconnect();
            this.fadeInObserver = null;
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -8% 0px',
      }
    );

    this.fadeInObserver.observe(this.sectionEl);
  }

  setupTrack() {
    Array.from(this.track.querySelectorAll('[data-limo-hero-clone="true"]')).forEach((node) => node.remove());

    const items = Array.from(this.track.children).filter((item) => item.dataset.limoHeroClone !== 'true');
    if (items.length === 0) return;

    this.originalWidth = this.track.scrollWidth;
    if (!this.originalWidth) {
      this.sectionEl.querySelectorAll('.limo-hero__image').forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', this.handleResize, { once: true });
        }
      });
      this.scheduleSetupRetry();
      return;
    }

    this.setupRetryCount = 0;

    this.loopBufferSets = Math.max(1, Math.ceil((this.viewport.clientWidth || 0) / this.originalWidth));

    for (let i = 0; i < this.loopBufferSets; i += 1) {
      const leadingFragment = document.createDocumentFragment();
      items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.dataset.limoHeroClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        leadingFragment.appendChild(clone);
      });
      this.track.insertBefore(leadingFragment, this.track.firstChild);
    }

    for (let i = 0; i < this.loopBufferSets; i += 1) {
      items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.dataset.limoHeroClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clone);
      });
    }

    this.minScroll = this.originalWidth * this.loopBufferSets;
    this.maxScroll = this.minScroll + this.originalWidth;
    this.maxNativeScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);

    if (this.maxScroll > this.maxNativeScroll) {
      this.maxScroll = this.maxNativeScroll;
    }
    if (this.minScroll > this.maxScroll) {
      this.minScroll = Math.max(0, this.maxScroll - this.originalWidth);
    }

    if (this.originalWidth > 0) {
      if (this.autoScrollLeft < this.minScroll || this.autoScrollLeft > this.maxScroll) {
        this.autoScrollLeft = this.minScroll;
      }
    }

    this.sectionEl.querySelectorAll('.limo-hero__image').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', this.handleResize, { once: true });
      }
    });
  }

  scheduleSetupRetry() {
    if (this.setupRetryId || this.setupRetryCount >= this.maxSetupRetries) return;
    this.setupRetryId = requestAnimationFrame(() => {
      this.setupRetryId = null;
      this.setupRetryCount += 1;
      this.handleResize();
    });
  }

  bindEvents() {
    this.syncInputModeBindings();

    this.root.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.root.addEventListener('touchmove', this.handleTouchMove, { passive: true });
    this.root.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    this.root.addEventListener('touchcancel', this.handleTouchEnd, { passive: true });
    window.addEventListener('touchend', this.handleGlobalTouchEnd, { passive: true });
    window.addEventListener('touchcancel', this.handleGlobalTouchEnd, { passive: true });
    window.addEventListener('resize', this.handleResize);

    if (typeof this.modeMediaQuery.addEventListener === 'function') {
      this.modeMediaQuery.addEventListener('change', this.handleInputModeChange);
    } else if (typeof this.modeMediaQuery.addListener === 'function') {
      this.modeMediaQuery.addListener(this.handleInputModeChange);
    }
  }

  syncInputModeBindings() {
    if (this.isMouseMode) {
      if (this.mouseEventsBound) return;
      this.sectionEl.classList.add('limo-hero--mouse-enabled');
      this.root.addEventListener('mouseenter', this.handleMouseEnter);
      this.root.addEventListener('mouseleave', this.handleMouseLeave);
      this.root.addEventListener('mousemove', this.handleMouseMove);
      this.root.addEventListener('wheel', this.handleWheel, { passive: false });
      this.root.addEventListener('mousedown', this.handleMouseDown);
      window.addEventListener('mouseup', this.handleMouseUp);
      this.root.addEventListener('mousemove', this.handleMouseDrag);
      this.mouseEventsBound = true;
      return;
    }

    if (this.mouseEventsBound) {
      this.sectionEl.classList.remove('limo-hero--mouse-enabled');
      this.sectionEl.classList.remove('limo-hero--hovered');
      this.sectionEl.classList.remove('limo-hero--dragging');
      this.isHovered = false;
      this.isDragging = false;
      this.root.removeEventListener('mouseenter', this.handleMouseEnter);
      this.root.removeEventListener('mouseleave', this.handleMouseLeave);
      this.root.removeEventListener('mousemove', this.handleMouseMove);
      this.root.removeEventListener('wheel', this.handleWheel);
      this.root.removeEventListener('mousedown', this.handleMouseDown);
      window.removeEventListener('mouseup', this.handleMouseUp);
      this.root.removeEventListener('mousemove', this.handleMouseDrag);
      this.mouseEventsBound = false;
    }
  }

  handleInputModeChange(event) {
    const nextMouseMode = !!event.matches;
    if (nextMouseMode === this.isMouseMode) return;
    this.isMouseMode = nextMouseMode;
    this.syncInputModeBindings();
  }

  handleMouseEnter() {
    this.isHovered = true;
    this.sectionEl.classList.add('limo-hero--hovered');
  }

  handleMouseLeave() {
    this.isHovered = false;
    this.sectionEl.classList.remove('limo-hero--hovered');
  }

  handleMouseMove(event) {
    const rect = this.root.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    this.mouseXRatio = Math.min(Math.max(x / rect.width, 0), 1);

    this.cursor.style.transform =
      'translate3d(' + event.clientX + 'px, ' + event.clientY + 'px, 0) translate(-50%, -50%)';
  }

  handleWheel(event) {
    const absX = Math.abs(event.deltaX);
    const absY = Math.abs(event.deltaY);
    const horizontalIntent = absX > absY || (event.shiftKey && absY > 0);

    if (!horizontalIntent) {
      return;
    }

    event.preventDefault();
    const delta = absX > absY ? event.deltaX : event.deltaY;
    this.viewport.scrollLeft += delta;
    this.autoScrollLeft = this.viewport.scrollLeft;
    this.normalizeScroll();
  }

  handleMouseDown(event) {
    if (event.button !== 0) return;
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.scrollStartX = this.viewport.scrollLeft;
    this.autoScrollLeft = this.viewport.scrollLeft;
    this.sectionEl.classList.add('limo-hero--dragging');
    event.preventDefault();
  }

  handleMouseUp() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.sectionEl.classList.remove('limo-hero--dragging');
  }

  handleTouchStart(event) {
    const touch = event.touches && event.touches[0];
    if (!touch) return;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.isTouchInteracting = false;
    this.lastTouchMoveAt = performance.now();
  }

  handleTouchMove(event) {
    const touch = event.touches && event.touches[0];
    if (!touch) return;
    const deltaX = Math.abs(touch.clientX - this.touchStartX);
    const deltaY = Math.abs(touch.clientY - this.touchStartY);
    const isHorizontalSwipe = deltaX > this.touchMoveThreshold && deltaX > deltaY;
    this.lastTouchMoveAt = performance.now();
    if (isHorizontalSwipe) {
      this.isTouchInteracting = true;
    }
  }

  handleTouchEnd() {
    this.autoScrollLeft = this.viewport.scrollLeft;
    this.isTouchInteracting = false;
    this.lastTouchMoveAt = 0;
  }

  handleGlobalTouchEnd() {
    this.autoScrollLeft = this.viewport.scrollLeft;
    this.isTouchInteracting = false;
    this.lastTouchMoveAt = 0;
  }

  handleResize() {
    const previousOriginalWidth = this.originalWidth || 1;
    const previousOffset = this.viewport.scrollLeft - this.minScroll;
    const previousProgress = previousOriginalWidth > 0 ? previousOffset / previousOriginalWidth : 0;

    this.setupTrack();

    if (this.originalWidth > 0) {
      this.autoScrollLeft = this.minScroll + previousProgress * this.originalWidth;
      this.viewport.scrollLeft = this.autoScrollLeft;
      this.normalizeScroll();
    }
  }

  handleMouseDrag(event) {
    if (!this.isDragging) return;
    const deltaX = event.clientX - this.dragStartX;
    this.viewport.scrollLeft = this.scrollStartX - deltaX;
    this.autoScrollLeft = this.viewport.scrollLeft;
    this.normalizeScroll();
  }

  normalizeScroll() {
    if (!this.originalWidth) return;
    this.maxNativeScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    const loopSpan = this.maxScroll - this.minScroll;
    if (loopSpan <= 0) return;
    const epsilon = 0.5;
    let current = this.autoScrollLeft;

    if (!Number.isFinite(current)) {
      current = this.viewport.scrollLeft;
    }

    if (current >= this.maxScroll - epsilon || current >= this.maxNativeScroll - epsilon) {
      current = this.minScroll + ((current - this.minScroll) % loopSpan);
    } else if (current < this.minScroll) {
      const delta = (this.minScroll - current) % loopSpan;
      current = this.maxScroll - delta;
    }

    this.autoScrollLeft = current;
    this.viewport.scrollLeft = current;
  }

  startAnimation() {
    const step = (timestamp) => {
      if (!this.viewport) return;
      const now = typeof timestamp === 'number' ? timestamp : performance.now();
      if (!this.lastFrameTime) {
        this.lastFrameTime = now;
      }
      const deltaRatio = Math.min((now - this.lastFrameTime) / 16.667, 2);
      this.lastFrameTime = now;

      if (this.originalWidth > 0 && this.viewport.scrollLeft < this.minScroll) {
        this.autoScrollLeft = this.minScroll;
        this.viewport.scrollLeft = this.minScroll;
      }

      if (
        this.isTouchInteracting &&
        this.lastTouchMoveAt > 0 &&
        now - this.lastTouchMoveAt > this.touchResumeDelayMs
      ) {
        this.isTouchInteracting = false;
      }

      if (!this.isDragging) {
        const extraSpeed = this.isMouseMode && this.isHovered ? this.mouseXRatio * this.maxExtraSpeed : 0;
        const modeMultiplier = this.isMouseMode ? 1 : this.touchAutoSpeedMultiplier;
        const speed = (this.baseSpeed + extraSpeed) * deltaRatio * modeMultiplier;
        if (!this.isTouchInteracting) {
          const previousScrollLeft = this.viewport.scrollLeft;
          this.autoScrollLeft += speed;
          this.viewport.scrollLeft = this.autoScrollLeft;
          this.normalizeScroll();

          const appliedDelta = Math.abs(this.viewport.scrollLeft - previousScrollLeft);
          if (appliedDelta < 0.01) {
            this.stallFrameCount += 1;
            if (this.stallFrameCount >= this.stallFrameThreshold) {
              this.autoScrollLeft += this.stallNudgePx;
              this.viewport.scrollLeft = this.autoScrollLeft;
              this.normalizeScroll();
              this.stallFrameCount = 0;
            }
          } else {
            this.stallFrameCount = 0;
          }
        } else {
          this.stallFrameCount = 0;
        }
      }

      this.rafId = requestAnimationFrame(step);
    };

    this.rafId = requestAnimationFrame(step);
    if (this.originalWidth > 0) {
      this.autoScrollLeft = this.minScroll;
      this.viewport.scrollLeft = this.minScroll;
    }
  }

  destroy() {
    this.root.removeEventListener('mouseenter', this.handleMouseEnter);
    this.root.removeEventListener('mouseleave', this.handleMouseLeave);
    this.root.removeEventListener('mousemove', this.handleMouseMove);
    this.root.removeEventListener('wheel', this.handleWheel);
    this.root.removeEventListener('mousedown', this.handleMouseDown);
    window.removeEventListener('mouseup', this.handleMouseUp);
    this.root.removeEventListener('mousemove', this.handleMouseDrag);
    this.root.removeEventListener('touchstart', this.handleTouchStart);
    this.root.removeEventListener('touchmove', this.handleTouchMove);
    this.root.removeEventListener('touchend', this.handleTouchEnd);
    this.root.removeEventListener('touchcancel', this.handleTouchEnd);
    window.removeEventListener('touchend', this.handleGlobalTouchEnd);
    window.removeEventListener('touchcancel', this.handleGlobalTouchEnd);
    window.removeEventListener('resize', this.handleResize);
    this.sectionEl.classList.remove('limo-hero--mouse-enabled');

    if (typeof this.modeMediaQuery.removeEventListener === 'function') {
      this.modeMediaQuery.removeEventListener('change', this.handleInputModeChange);
    } else if (typeof this.modeMediaQuery.removeListener === 'function') {
      this.modeMediaQuery.removeListener(this.handleInputModeChange);
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    if (this.setupRetryId) {
      cancelAnimationFrame(this.setupRetryId);
      this.setupRetryId = null;
    }

    if (this.fadeInObserver) {
      this.fadeInObserver.disconnect();
      this.fadeInObserver = null;
    }
  }
}

function initLimoHeroForSection(sectionEl) {
  const sectionId = sectionEl.dataset.sectionId;
  if (window.LimoHeroSections[sectionId]) {
    return;
  }
  const instance = new LimoHero(sectionEl);
  window.LimoHeroSections[sectionId] = instance;
}

function bootLimoHeroes() {
  document.querySelectorAll('.limo-hero-section').forEach(function (sectionEl) {
    initLimoHeroForSection(sectionEl);
  });
}

bootLimoHeroes();

document.addEventListener('shopify:section:load', function (event) {
  const sectionEl = event.target;
  if (sectionEl && sectionEl.classList.contains('limo-hero-section')) {
    initLimoHeroForSection(sectionEl);
  }
});

document.addEventListener('shopify:section:unload', function (event) {
  const sectionEl = event.target;
  if (!sectionEl || !sectionEl.dataset.sectionId) return;
  const sectionId = sectionEl.dataset.sectionId;
  const instance = window.LimoHeroSections[sectionId];
  if (instance && typeof instance.destroy === 'function') {
    instance.destroy();
  }
  delete window.LimoHeroSections[sectionId];
});

